/**
 * components/Textarea
 * The multi-line field. Used for exactly three things — a rejection reason, a
 * waive reason and a transaction memo — and all three are consequential, so it
 * shows a character counter once the input is past half its limit rather than
 * silently truncating at submit.
 */
import { useState } from 'react';
import { StyleSheet, Text, TextInput, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { noFocusOutline } from '../../theme/webStyle';
import { radii } from '../../theme/radii';
import { sans, size as fontSize, leading } from '../../theme/typography';

export interface TextareaProps {
  label?: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  hint?: string;
  maxLength?: number;
  rows?: number;
  disabled?: boolean;
}

export function Textarea({
  label,
  value,
  onChangeText,
  placeholder,
  required = false,
  hint,
  maxLength,
  rows = 3,
  disabled = false,
}: TextareaProps) {
  const [focused, setFocused] = useState(false);
  // Only appears when the limit is actually in play — a counter sitting at
  // "0 / 1000" is noise on a field nobody will fill.
  const showCounter = maxLength !== undefined && value.length > maxLength / 2;

  return (
    <View style={styles.field}>
      {!!label && (
        <Text style={styles.label}>
          {label}
          {required && <Text style={styles.required}> · required</Text>}
        </Text>
      )}
      {!!hint && <Text style={styles.hint}>{hint}</Text>}

      <TextInput
        value={value}
        onChangeText={onChangeText}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        editable={!disabled}
        placeholder={placeholder}
        placeholderTextColor={neutral.grey400}
        multiline
        numberOfLines={rows}
        maxLength={maxLength}
        textAlignVertical="top"
        style={[
          styles.input,
          {
            borderColor: focused ? neutral.ink : neutral.border,
            minHeight: rows * leading(fontSize.body) + 18,
          },
          disabled && styles.disabled,
        ]}
      />

      {showCounter && (
        <Text style={styles.counter}>
          {value.length} / {maxLength}
        </Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  field: { gap: 7 },
  label: { ...sans('500'), fontSize: fontSize.label, color: neutral.ink2 },
  required: { ...sans('400'), color: neutral.grey400 },
  hint: { ...sans('400'), fontSize: fontSize.meta, color: neutral.grey600 },
  input: {
    ...sans('400'),
    fontSize: fontSize.body,
    lineHeight: leading(fontSize.body),
    color: neutral.ink,
    paddingHorizontal: 11,
    paddingVertical: 9,
    borderWidth: 1,
    borderRadius: radii.control,
    backgroundColor: neutral.surface,
    ...noFocusOutline,
  },
  disabled: { backgroundColor: neutral.surface2, opacity: 0.7 },
  counter: { ...sans('400'), fontSize: fontSize.tableHeader, color: neutral.grey400, alignSelf: 'flex-end' },
});

export default Textarea;
